"use client"

import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import Image from "next/image"
import { useEffect, useRef, useState } from "react"
import Container from "ui/Container"
import {
  extractProperty,
  getNonUmbracoUrl,
  Link,
  replaceRichTextLinks,
} from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

const StyledDiv = styled.div<{ $paddingTop?: boolean }>`
  width: 100%;
  ${({ $paddingTop, theme }) =>
    $paddingTop &&
    `
    padding-top: 40px;
    ${theme.breakpoints.up("md")} {
      padding-top: 88px;
    }
  `}
`

const StyledTabsDiv = styled.div`
  position: sticky;
  top: 64px;
  z-index: 100;
  background-color: white;
  ${({ theme }) => theme.breakpoints.up("md")} {
    top: 80px;
  }
`

const StyledTabContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  flex-wrap: wrap;

  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 24px;
  }
`

const StyledTabButton = styled(Button)<{ isActive: boolean }>`
  padding: 16px 5px 16px 5px;
  min-width: 70px;
  min-height: 48px;
  height: auto;

  ${({ theme }) => theme.breakpoints.up("md")} {
    min-width: 159px;
    padding: 16px;
  }

  font-size: 20px;
  font-weight: 400;
  border: 1px solid #d1d5db;
  background-color: ${(p) => (p.isActive ? "#008761" : "white")};
  color: ${(p) => (p.isActive ? "white" : "#051711")};
`

const StyledInnerContentDiv = styled.div`
  padding-top: 32px;
  padding-bottom: 48px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 48px;
    padding-bottom: 64px;
  }
`

const StyeledItem = styled.div`
  padding-top: 24px;
  padding-bottom: 24px;
  border-bottom: 1px solid #d1d5db;
  ${({ theme }) => theme.breakpoints.up("md")} {
    border-top: 1px solid #d1d5db;
    border-bottom: none;
    padding-top: 32px;
    padding-bottom: 32px;
  }
`
const StyeledItemMain = styled.div`
  padding-top: 32px;
  ${({ theme }) => theme.breakpoints.down("md")} {
    > div:last-child {
      border-bottom: none;
    }
    > div:first-child {
      padding-top: 0px;
    }
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding-top: 48px;
    > div:last-child {
      border-bottom: 1px solid #d1d5db;
    }
  }
`

const StyledOpeningHour = styled.div`
  width: 300px;
  flex-shrink: 0;
  ${({ theme }) => theme.breakpoints.down("md")} {
    width: 100%;
  }
`

const StyledOpeningHourWrapper = styled.div`
  display: flex;
`

const StyledRowdiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    gap: 80px;
    align-items: flex-start;
  }
`

const StyledLocationDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`

const AddressLink = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
  a {
    text-decoration: none;
  }
`

const StyledLocAdressWrapper = styled.div`
  display: flex;
  flex-direction: column;
`

const StyledAccessibleDiv = styled.div`
  display: flex;
  flex-direction: row;
  gap: 4px;
  align-items: center;
`

const StyledMainDiv = styled.div`
  display: flex;
  flex-direction: column;
  ${({ theme }) => theme.breakpoints.down("md")} {
    gap: 24px;
    padding-top: 24px;
  }
  ${({ theme }) => theme.breakpoints.up("md")} {
    flex-direction: row;
    justify-content: space-between;
  }
`

const InfoBox = styled.div`
  display: flex;
  padding: 24px;
  flex-direction: column;
  justify-content: center;
  gap: 16px;
  border-radius: 12px;
  background: rgba(27, 189, 119, 0.08);
  margin-bottom: 32px;
  ${({ theme }) => theme.breakpoints.up("md")} {
    gap: 32px;
    margin-bottom: 48px;
  }
`
const TopBox = styled.div`
  display: flex;
  gap: 16px;
  flex-direction: column;
`

const InnerContent = styled.div`
  gap: 24px;
  display: flex;
  flex-direction: column;
`

const InnerInfoBox = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
  border-radius: 12px;
  background: rgba(27, 189, 119, 0.08);
  ${({ theme }) => theme.breakpoints.up("md")} {
    padding: 24px;
    gap: 16px;
  }
`

const StyledDescription = styled(Typography)`
  p,
  ul,
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    margin: 0;
  }
  ul {
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  &.collapsed ul li:nth-of-type(n + 4) {
    display: none;
  }
`

const StyledReadMoreButton = styled(Button)`
  padding: 0 !important;
  width: fit-content;
  height: fit-content;
  min-width: 0 !important;
  text-decoration: underline;
  margin-top: 32px;
  margin-left: 20px;
  :hover {
    background-color: transparent;
    text-decoration: underline;
  }
`

type Props = {
  data: any
  initialTab?: string
  paddingTop?: boolean
}

const RavKavServiceStationBlock = ({ data, initialTab, paddingTop }: Props) => {
  const itemsList = extractProperty(data, "itemsList")
  const [activeTab, setActiveTab] = useState<string>("")
  const [selectedData, setSelectedData] = useState<any>([])
  const [isExpanded, setIsExpanded] = useState(false)
  const infoContentRef = useRef<HTMLDivElement>(null)

  // Current item from itemsList based on active tab
  const currentItem = itemsList?.find(
    (item: any) => extractProperty(item, "tab") === activeTab,
  )
  const infoContent = extractProperty(currentItem, "infoContent")
  const infoTitle = extractProperty(infoContent?.[0], "title")
  const infoSubTitle = extractProperty(infoContent?.[0], "subTitle")
  const infoText = extractProperty(infoContent?.[0], "infoContent")

  // Count list items in infoText
  const hasMoreThanThreeItems = () => {
    if (!infoText) return false
    const tempDiv = document.createElement("div")
    tempDiv.innerHTML = infoText
    const listItems = tempDiv.querySelectorAll("ul li")
    return listItems.length > 3
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      // Remove the '#' character from the hash before setting it.
      const currentHash = window.location.hash.replace("#", "")
      const filtered = currentHash
        ? itemsList?.filter(
            (item: any) => extractProperty(item, "encorID") === currentHash,
          )
        : [itemsList?.[0]]
      const tab = extractProperty(filtered?.[0], "tab") || initialTab
      setActiveTab(tab)

      const filteredItemsList = extractProperty(filtered?.[0], "itemsList")
      setSelectedData(filteredItemsList || [])
    }
  }, [itemsList, initialTab])

  // Reset expanded state when active tab changes
  useEffect(() => {
    setIsExpanded(false)
  }, [activeTab])

  const HandleButtonClick = (tab: string) => {
    setActiveTab(tab)
    const filtered = itemsList?.filter(
      (item: any) => extractProperty(item, "tab") === tab,
    )
    const filteredItemsList = extractProperty(filtered?.[0], "itemsList")
    const encorID = extractProperty(filtered?.[0], "encorID") || ""
    setSelectedData(filteredItemsList || [])

    if (typeof window !== "undefined") {
      // Replace the current hash in the URL with the new encorID.
      window.history.replaceState(null, "", `#${encorID}`)
    }
  }

  const tInner = useTranslations("InnerPages")

  const handleReadMoreClick = () => {
    setIsExpanded(!isExpanded)
  }

  return (
    <StyledDiv $paddingTop={paddingTop}>
      <StyledTabsDiv>
        <Container paddingMobile>
          <StyledTabContainer>
            {itemsList?.map((item: any, index: number) => {
              const tab = extractProperty(item, "tab")

              return (
                <StyledTabButton
                  key={index}
                  isActive={tab === activeTab}
                  onClick={() => HandleButtonClick(tab)}
                >
                  {tab}
                </StyledTabButton>
              )
            })}
          </StyledTabContainer>
        </Container>
      </StyledTabsDiv>
      <Container paddingMobile>
        <StyledInnerContentDiv>
          {infoTitle || infoText ? (
            <InfoBox>
              <TopBox>
                <Typography variant="body1" fontWeight={700}>
                  {infoTitle}
                </Typography>
                <Typography variant="body2">{infoSubTitle}</Typography>
                <div ref={infoContentRef}>
                  <StyledDescription
                    variant="body2"
                    component="div"
                    className={isExpanded ? "" : "collapsed"}
                    dangerouslySetInnerHTML={{
                      __html: replaceRichTextLinks(infoText),
                    }}
                  />
                  {hasMoreThanThreeItems() && !isExpanded ? (
                    <StyledReadMoreButton
                      onClick={handleReadMoreClick}
                      disableRipple
                      variant="text"
                    >
                      <Typography variant="body2">הצג עוד</Typography>
                    </StyledReadMoreButton>
                  ) : null}
                </div>
              </TopBox>
            </InfoBox>
          ) : null}
          <Typography variant="body2">
            {tInner("weFoundStations", { count: selectedData?.length || 0 })}
          </Typography>
          <StyeledItemMain>
            {selectedData &&
              selectedData?.map((item: any, index: number) => {
                const title = extractProperty(item, "title")
                const itemsList = extractProperty(item, "itemsList")
                const locationTitle = extractProperty(item, "locationTitle")
                const loacationIcon = extractProperty(item, "loacationIcon")
                const address = extractProperty(item, "address")
                const accessibleText = extractProperty(item, "accessibleText")
                const accessibleIcon = extractProperty(item, "accessibleIcon")
                const addressLink = extractProperty(item, "addressLink")
                const importantInfo = extractProperty(item, "importantInfo")
                return (
                  <StyeledItem key={index}>
                    <Typography variant="subtitle1">{title}</Typography>
                    <InnerContent>
                      <StyledMainDiv>
                        <StyledRowdiv>
                          <StyledOpeningHour>
                            {itemsList?.map((item: any, index: number) => {
                              const day = extractProperty(item, "day")
                              const hour = extractProperty(item, "hour")
                              return (
                                <StyledOpeningHourWrapper key={index}>
                                  <Typography
                                    variant="body2"
                                    sx={{ flexShrink: 0 }}
                                  >
                                    {day}
                                  </Typography>
                                  &nbsp;
                                  <Typography
                                    variant="body2"
                                    sx={{ flexShrink: 0 }}
                                  >
                                    {hour}
                                  </Typography>
                                </StyledOpeningHourWrapper>
                              )
                            })}
                          </StyledOpeningHour>
                          <StyledLocAdressWrapper>
                            <StyledLocationDiv>
                              <AddressLink>
                                <Image
                                  src={getUmbracoMediaUrl(loacationIcon)}
                                  width={20}
                                  height={20}
                                  alt="location icon"
                                />
                                <Link
                                  href={getNonUmbracoUrl(addressLink?.[0]?.url)}
                                  target={addressLink?.[0]?.target}
                                >
                                  <Typography
                                    variant="body1"
                                    style={{ fontWeight: 700 }}
                                  >
                                    {locationTitle}
                                  </Typography>
                                </Link>
                              </AddressLink>
                              <Link
                                href={getNonUmbracoUrl(addressLink?.[0]?.url)}
                                target={addressLink?.[0]?.target}
                              >
                                <Typography
                                  variant="body2"
                                  style={{ textDecoration: "underline" }}
                                >
                                  {addressLink?.[0]?.name}
                                </Typography>
                              </Link>
                            </StyledLocationDiv>
                            <Typography
                              variant="body2"
                              style={{ textDecoration: "underline" }}
                            >
                              {address}
                            </Typography>
                          </StyledLocAdressWrapper>
                        </StyledRowdiv>
                        <StyledAccessibleDiv>
                          <Image
                            src={getUmbracoMediaUrl(accessibleIcon)}
                            width={20}
                            height={20}
                            alt="accessible"
                          />
                          <Typography variant="body2">
                            {accessibleText}
                          </Typography>
                        </StyledAccessibleDiv>
                      </StyledMainDiv>
                      {importantInfo && (
                        <InnerInfoBox>
                          <StyledDescription
                            variant="body2"
                            component="div"
                            dangerouslySetInnerHTML={{
                              __html: replaceRichTextLinks(importantInfo),
                            }}
                          />
                        </InnerInfoBox>
                      )}
                    </InnerContent>
                  </StyeledItem>
                )
              })}
          </StyeledItemMain>
        </StyledInnerContentDiv>
      </Container>
    </StyledDiv>
  )
}

export default RavKavServiceStationBlock
